import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { invoicesService, api } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { Badge } from '../../components/UI/Badge';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import { Pagination } from '../../components/UI/Pagination';
import { Spinner } from '../../components/UI/Spinner';
import { EmptyState } from '../../components/UI/EmptyState';
import {
  Receipt,
  Plus,
  Search,
  Eye,
  Edit2,
  Trash2,
  Download,
  ExternalLink,
} from 'lucide-react';

export const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const { success, error: toastError } = useToast();
  const navigate = useNavigate();

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await invoicesService.list({ search, page, limit: 15 });
      if (res && res.data) {
        if (res.data.data) {
          setInvoices(res.data.data);
          setPagination({
            currentPage: res.data.current_page,
            lastPage: res.data.last_page,
            total: res.data.total,
            from: res.data.from,
            to: res.data.to,
          });
        } else {
          setInvoices(Array.isArray(res.data) ? res.data : []);
        }
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar facturas.');
    } finally {
      setLoading(false);
    }
  }, [search, page, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInvoices();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchInvoices]);

  const handleDelete = async () => {
    if (!invoiceToDelete) return;
    setSubmitting(true);
    try {
      await invoicesService.delete(invoiceToDelete.id);
      success('Factura eliminada correctamente.');
      setDeleteConfirmOpen(false);
      setInvoiceToDelete(null);
      fetchInvoices();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar factura.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPdf = async (inv) => {
    try {
      await invoicesService.downloadPdf(inv.id, inv.number);
      success('PDF de factura descargado.');
    } catch (err) {
      toastError('No se pudo descargar el archivo PDF.');
    }
  };

  const handleOpenPdf = (inv) => {
    window.open(api.getPdfUrl('invoices', inv.id), '_blank');
  };

  const getStatusBadge = (status) => {
    switch (Number(status)) {
      case 1:
        return <Badge variant="success" size="sm" dot>Pagada</Badge>;
      case 2:
        return <Badge variant="default" size="sm">Cerrada</Badge>;
      default:
        return <Badge variant="warning" size="sm" dot>Abierta</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
            <Receipt className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
            Facturación & Cobros
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Control de cuentas por cobrar, estados de pago y emisión de facturas
          </p>
        </div>

        <Link to="/invoices/create">
          <Button icon={Plus} className="bg-[#08707A] hover:bg-[#065B63]">
            Nueva Factura
          </Button>
        </Link>
      </div>

      {/* List Card */}
      <Card>
        <CardHeader>
          <div className="w-full sm:w-80">
            <Input
              placeholder="Buscar por número o cliente..."
              icon={Search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="py-1.5"
            />
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {loading ? (
            <Spinner label="Cargando facturas..." className="py-12" />
          ) : invoices.length === 0 ? (
            <EmptyState
              icon={Receipt}
              title="No hay facturas emitidas"
              description={search ? "No se encontraron facturas para los términos de búsqueda." : "Genera tu primera factura para comenzar a registrar pagos y cobros."}
              actionLabel="Crear Factura"
              onAction={() => navigate('/invoices/create')}
              actionIcon={Plus}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4">Número</th>
                    <th className="py-3 px-4">Cliente</th>
                    <th className="py-3 px-4">Fecha Emisión</th>
                    <th className="py-3 px-4">Vencimiento</th>
                    <th className="py-3 px-4">Total</th>
                    <th className="py-3 px-4 text-center">Estado</th>
                    <th className="py-3 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {invoices.map((inv) => (
                    <tr
                      key={inv.id}
                      className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3.5 px-4 font-mono font-bold text-slate-900 dark:text-slate-100">
                        #{inv.number || String(inv.id).padStart(6, '0')}
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="font-bold text-slate-900 dark:text-slate-100">
                          {inv.customer?.full_name || 'Cliente sin asignar'}
                        </div>
                        {inv.customer?.email && (
                          <div className="text-[11px] text-slate-400">{inv.customer.email}</div>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300">
                        {inv.invo_date || '—'}
                      </td>

                      <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300">
                        {inv.due_date || '—'}
                      </td>

                      <td className="py-3.5 px-4 font-black text-sm text-slate-900 dark:text-slate-100">
                        {inv.currency?.symbol || '$'} {Number(inv.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>

                      <td className="py-3.5 px-4 text-center">
                        {getStatusBadge(inv.status)}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link to={`/invoices/${inv.id}`}>
                            <Button variant="ghost" size="sm" icon={Eye} title="Ver Detalle y Pagos" />
                          </Link>

                          <Button
                            variant="ghost"
                            size="sm"
                            icon={ExternalLink}
                            onClick={() => handleOpenPdf(inv)}
                            title="Ver PDF en navegador"
                          />

                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Download}
                            onClick={() => handleDownloadPdf(inv)}
                            title="Descargar PDF"
                          />

                          <Link to={`/invoices/edit/${inv.id}`}>
                            <Button variant="ghost" size="sm" icon={Edit2} title="Editar" />
                          </Link>

                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Trash2}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                            onClick={() => {
                              setInvoiceToDelete(inv);
                              setDeleteConfirmOpen(true);
                            }}
                            title="Eliminar"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={pagination.currentPage}
            lastPage={pagination.lastPage}
            total={pagination.total}
            from={pagination.from}
            to={pagination.to}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>

      {/* Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="¿Eliminar factura?"
        message={`¿Estás seguro de que deseas eliminar la factura #${invoiceToDelete?.number}? Se eliminarán los registros de pago e historial asociados.`}
        confirmText="Eliminar Factura"
        isLoading={submitting}
      />
    </div>
  );
};
